import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const when = (s) => (s ? new Date(s).toLocaleString("en-US", { timeZone: "America/New_York", dateStyle: "short", timeStyle: "short" }) : "");

async function downloadCsv(dataset) {
  const token = localStorage.getItem("fb_token");
  const res = await fetch(`${API}/reports/audit/csv?dataset=${dataset}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) { toast.error("CSV download failed"); return; }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a"); a.href = url; a.download = `food_bazaar_audit_${dataset}.csv`; a.click();
  URL.revokeObjectURL(url);
}

const CSV_LABEL = { activity: "Activity", cash: "Cash transactions", cash_by_employee: "Cash by employee", card: "Card reloads", stripe: "Stripe payments", staff: "Staff", exceptions: "Exceptions", reconciliation: "Reconciliation" };

const CsvBtn = ({ ds }) => (
  <Button size="sm" variant="outline" onClick={() => downloadCsv(ds)} className="bg-slate-900 border-slate-700 text-slate-200 h-8" data-testid={`audit-csv-${ds}`}>
    <Download size={13} className="mr-1" /> {CSV_LABEL[ds]} CSV
  </Button>
);

const Tag = ({ kind }) => {
  const s = { CONFIRMED: "bg-emerald-500/15 text-emerald-300", CALCULATED: "bg-sky-500/15 text-sky-300", MISSING: "bg-slate-600/30 text-slate-300", UNRESOLVED: "bg-rose-500/15 text-rose-300" }[kind];
  return <span className={`text-[9px] tracking-widest font-bold px-2 py-0.5 rounded ${s}`}>{kind}</span>;
};

function Section({ title, tag, ds, children, tid }) {
  return (
    <section className="bg-[#131D33] border border-slate-800 rounded-2xl p-5 space-y-4" data-testid={tid}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2"><h3 className="font-display text-xl font-black text-white">{title}</h3>{tag && <Tag kind={tag} />}</div>
        {ds && <div className="flex gap-2 flex-wrap">{[].concat(ds).map((d) => <CsvBtn key={d} ds={d} />)}</div>}
      </div>
      {children}
    </section>
  );
}

const Stat = ({ label, value, tid }) => (
  <div className="rounded-xl bg-[#0B1120] border border-slate-800 p-4">
    <div className="text-[10px] tracking-widest uppercase text-slate-400 font-bold">{label}</div>
    <div className="font-display text-3xl font-black text-white mt-1" data-testid={tid}>{value}</div>
  </div>
);

function Grid({ cols, rows, tid, empty = "No records." }) {
  if (!rows.length) return <div className="text-sm text-slate-500" data-testid={tid}>{empty}</div>;
  return (
    <div className="overflow-x-auto max-h-96 rounded-lg border border-slate-800" data-testid={tid}>
      <table className="w-full text-sm">
        <thead className="bg-[#0B1120] sticky top-0"><tr>{cols.map(([, h]) => <th key={h} className="text-left px-3 py-2 text-[10px] tracking-widest uppercase text-slate-400 whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-slate-800">
          {rows.map((r, i) => <tr key={i}>{cols.map(([k, h, f]) => <td key={h} className="px-3 py-2 text-slate-200 whitespace-nowrap">{f ? f(r[k], r) : r[k]}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  );
}

function StaffChart({ staff }) {
  const max = Math.max(1, ...staff.map((s) => s.redemptions));
  return (
    <div className="space-y-2" data-testid="audit-staff-chart">
      <div className="flex gap-4 text-xs text-slate-400">
        <span className="flex items-center gap-1"><i className="w-3 h-3 rounded-sm bg-amber-400 inline-block" /> Beer</span>
        <span className="flex items-center gap-1"><i className="w-3 h-3 rounded-sm bg-rose-500 inline-block" /> Cocktail</span>
        <span className="flex items-center gap-1"><i className="w-3 h-3 rounded-sm bg-slate-500 inline-block" /> Type not recorded</span>
      </div>
      {staff.filter((s) => s.redemptions > 0).map((s) => {
        const other = s.redemptions - s.beer - s.cocktail;
        return (
          <div key={s.email} className="grid grid-cols-[6rem_1fr_2.5rem] sm:grid-cols-[12rem_1fr_3rem] items-center gap-3">
            <div className="text-xs text-slate-300 truncate" title={s.email}>{s.name || s.email}</div>
            <div className="flex h-5 rounded overflow-hidden bg-[#0B1120]" style={{ width: `${(s.redemptions / max) * 100}%` }}>
              <div className="bg-amber-400" style={{ flex: s.beer }} />
              <div className="bg-rose-500" style={{ flex: s.cocktail }} />
              <div className="bg-slate-500" style={{ flex: other }} />
            </div>
            <div className="text-right font-bold text-white text-sm">{s.redemptions}</div>
          </div>
        );
      })}
    </div>
  );
}

export function AuditReport() {
  const [a, setA] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/reports/audit").then((r) => setA(r.data)).catch(() => setErr("Could not load the audit.")); }, []);
  if (err) return <div className="text-rose-300">{err}</div>;
  if (!a) return <div className="flex items-center gap-2 text-slate-400"><Loader2 className="animate-spin" size={16} /> Building audit (includes a live Stripe check)…</div>;
  const T = a.activity_total, st = a.stripe;
  const types = ["REGULAR", "VIP", "EMPLOYEE"];
  return (
    <div className="space-y-5" data-testid="audit-report">
      <div>
        <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Read-only audit</div>
        <h2 className="font-display text-2xl font-black text-white">Event Audit Report</h2>
        <p className="text-xs text-slate-400">Generated {when(a.generated_at)} (Eastern) · Nothing in the database is changed.</p>
      </div>

      <Section title="Actual Event Activity" tag="CONFIRMED" ds="activity" tid="audit-activity">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat label="Unique Drink Tickets Used" value={T.unique_tickets_used} tid="audit-unique-used" />
          <Stat label="Total Drink Redemptions" value={T.redemptions} tid="audit-redemptions" />
          <Stat label="Beers / Cocktails" value={`${T.beer} / ${T.cocktail}`} tid="audit-beer-cocktail" />
          <Stat label="Tickets Generated (not attendance)" value={a.tickets_generated} tid="audit-generated" />
        </div>
        <Grid tid="audit-activity-table" rows={types.map((t) => ({ t, ...a.activity[t] }))} cols={[
          ["t", "Type"], ["unique_tickets_used", "Tickets used"], ["redemptions", "Drinks"], ["beer", "Beer"], ["cocktail", "Cocktail"],
          ["other", "Type not recorded"], ["used_with_purchased_credit", "Used + reloaded"], ["credit_redeemed", "Credit redeemed", money], ["credit_remaining", "Credit left", money],
        ]} />
        <p className="text-xs text-slate-500">Unique Drink Tickets Used = tickets with at least one drink served, counted once. Not attendance — use Eventbrite check-ins for that.</p>
      </Section>

      <Section title="Cash Sales" tag="CONFIRMED" ds={["cash", "cash_by_employee"]} tid="audit-cash">
        <div className="grid grid-cols-2 gap-3">
          <Stat label="Cash Reloads" value={a.cash.count} tid="audit-cash-count" />
          <Stat label="Cash Collected" value={money(a.cash.total)} tid="audit-cash-total" />
        </div>
        <Grid tid="audit-cash-by-employee" rows={a.cash.by_employee} cols={[["name", "Employee"], ["email", "Email"], ["count", "Reloads"], ["amount", "Cash", money]]} />
        <Grid tid="audit-cash-rows" rows={a.cash.rows} cols={[
          ["ticket_id", "Ticket"], ["ticket_type", "Type"], ["at", "When", when], ["amount", "Amount", money], ["beers", "Beer"], ["cocktails", "Cocktail"],
          ["added_as", "Added as"], ["processed_by", "Processed by"], ["transaction_id", "Transaction"], ["manual_adjustments", "Adjustments"],
        ]} />
        {a.unknown_payments.length > 0 && <p className="text-xs text-amber-300">{a.unknown_payments.length} reload(s) have no recorded payment method and are NOT counted as cash.</p>}
      </Section>

      <Section title="Card Sales (Stripe)" tag={st.available ? "CONFIRMED" : "MISSING"} ds={["card", "stripe"]} tid="audit-card">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat label="Card reloads in app" value={`${a.card.count} · ${money(a.card.db_total)}`} tid="audit-card-db" />
          <Stat label="Stripe paid — Food Bazaar" value={st.available ? money(st.food_bazaar_gross) : "n/a"} tid="audit-stripe-gross" />
          <Stat label="Refunds" value={st.available ? money(st.refunds) : "n/a"} tid="audit-stripe-refunds" />
          <Stat label="Net vs reported $425" value={st.available ? `${money(st.net)} (${st.difference_vs_reported >= 0 ? "+" : ""}${st.difference_vs_reported.toFixed(2)})` : "n/a"} tid="audit-stripe-net" />
        </div>
        <p className="text-xs text-slate-400">Staff-processed: {a.card.staff_count} ({money(a.card.staff_total)}) · Guest self-service (QR): {a.card.self_service_count} ({money(a.card.self_service_total)}) · Stripe mode: <b>{st.mode || "unknown"}</b> · Stripe checked from {when(st.since)}{st.error ? ` · Stripe error: ${st.error}` : ""}</p>
        <Grid tid="audit-stripe-rows" rows={st.rows} cols={[
          ["created", "When", when], ["id", "Stripe payment ID"], ["ticket_visible", "Ticket"], ["amount_received", "Paid", money], ["status", "Status"],
          ["refunded", "Refunded", money], ["source", "Source", (v, r) => (v === "GUEST_SELF_SERVICE" ? "Guest QR" : r.reference ? "Staff" : "Unknown")],
          ["match", "Database match", (v) => <span className={v === "MATCHED" ? "text-emerald-300" : v.startsWith("NOT PAID") ? "text-slate-400" : "text-rose-300"}>{v}</span>],
        ]} />
      </Section>

      <Section title="Staff Performance" tag="CONFIRMED" ds="staff" tid="audit-staff">
        <StaffChart staff={a.staff} />
        <Grid tid="audit-staff-rows" rows={a.staff} cols={[
          ["name", "Employee"], ["email", "Email"], ["redemptions", "Drinks served"], ["tickets", "Unique tickets"], ["beer", "Beer"], ["cocktail", "Cocktail"],
          ["REGULAR", "Regular"], ["VIP", "VIP"], ["EMPLOYEE", "Employee"], ["cash_count", "Cash reloads"], ["cash_amount", "Cash", money], ["card_count", "Card reloads"], ["card_amount", "Card", money],
        ]} />
      </Section>

      <Section title="Audit Exceptions" tag="UNRESOLVED" ds={["exceptions", "reconciliation"]} tid="audit-exceptions">
        <div className="flex flex-wrap gap-2">
          {Object.entries(a.exceptions.reduce((m, e) => ({ ...m, [e.type]: (m[e.type] || 0) + 1 }), {})).map(([k, n]) => (
            <span key={k} className="text-xs px-2 py-1 rounded bg-rose-500/10 border border-rose-500/30 text-rose-200">{k}: {n}</span>
          ))}
        </div>
        <Grid tid="audit-exception-rows" rows={a.exceptions} empty="No exceptions found." cols={[["type", "Type"], ["ticket_id", "Ticket"], ["detail", "Detail"]]} />
        <p className="text-xs text-slate-500">Per-ticket reconciliation ({a.reconciliation.length} tickets, {a.reconciliation.filter((r) => r.result !== "OK").length} mismatched) is in the Reconciliation CSV.</p>
      </Section>

      <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 space-y-1" data-testid="audit-limitations">
        <div className="flex items-center gap-2 text-amber-300 font-bold text-sm"><AlertTriangle size={16} /> Limitations &amp; missing information</div>
        {a.limitations.map((l) => <p key={l} className="text-xs text-amber-100/90">• {l}</p>)}
      </div>
    </div>
  );
}
